import { useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

/**
 * Every piece of shareable state lives in the URL, so a permanent link and a
 * QR code are the same thing and the back button works for free.
 *
 *   /                       everything
 *   /station/VLM_39         one station, zoomed and filtered
 *   /lake/Walloon           one waterbody group, map fitted to it
 *   /lake/Walloon?metric=…  …with a chosen metric
 *
 * `groupSegment` is the program's own word for its grouping dimension — the
 * three programs group by Lake, Stream and Waterbody respectively, and the
 * URL should say so rather than exposing a generic "group".
 */
export interface ViewState {
  /** Station ID from the path, e.g. "VLM_39". */
  stationId: string | null;
  /** Group value from the path, already URL-decoded, e.g. "Bear River". */
  group: string | null;
  /** Extra shareable state carried in the query string. */
  params: URLSearchParams;

  selectStation: (id: string | null) => void;
  selectGroup: (value: string | null) => void;
  setParam: (key: string, value: string | null) => void;
  clearAll: () => void;

  /** Absolute URL for the current view — what the QR code encodes. */
  permalink: string;
  /** Absolute URL for one station, independent of current state. */
  stationLink: (id: string) => string;
  /** Absolute URL for one group, independent of current state. */
  groupLink: (value: string) => string;
}

export function useViewState(groupSegment: string): ViewState {
  const { stationId: rawStation, group: rawGroup } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const stationId = rawStation ? decodeURIComponent(rawStation) : null;
  const group = rawGroup ? decodeURIComponent(rawGroup) : null;

  const origin = typeof window === "undefined" ? "" : window.location.origin;
  const qs = params.toString();
  const suffix = qs ? `?${qs}` : "";

  const selectStation = useCallback(
    (id: string | null) => {
      // Selecting a station clears the group: a station is the narrower view,
      // and keeping both would make the "clear" affordances ambiguous.
      navigate(id ? `/station/${encodeURIComponent(id)}${suffix}` : `/${suffix}`);
    },
    [navigate, suffix],
  );

  const selectGroup = useCallback(
    (value: string | null) => {
      navigate(value ? `/${groupSegment}/${encodeURIComponent(value)}${suffix}` : `/${suffix}`);
    },
    [navigate, groupSegment, suffix],
  );

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const clearAll = useCallback(() => navigate("/"), [navigate]);

  const permalink = useMemo(() => {
    if (typeof window === "undefined") return "";
    return origin + window.location.pathname + suffix;
  }, [origin, suffix, stationId, group]);

  const stationLink = useCallback(
    (id: string) => `${origin}/station/${encodeURIComponent(id)}`,
    [origin],
  );
  const groupLink = useCallback(
    (value: string) => `${origin}/${groupSegment}/${encodeURIComponent(value)}`,
    [origin, groupSegment],
  );

  return {
    stationId, group, params,
    selectStation, selectGroup, setParam, clearAll,
    permalink, stationLink, groupLink,
  };
}
